import {
	useCallback, useRef,
} from 'react';
import API from 'Vulture/lib/API.js';
import {
	formatDate,
	getNowForLoopDate,
	isPreviewingAnotherDay,
} from '../../utils/dateUtils';
import {
	shouldSkipSlide,
} from '../../utils/slides';
import {
	useSlideTransformation,
} from '../common/useSlideTransformation';
import {
	createBackfillIndicator,
} from '../../utils/vclUtils';


/**
 * VCL-specific slide data hook
 * Handles slide operations specific to Visual Content Loop functionality
 * Includes backfill calculation, loop date filtering, and provider-specific API calls
 */
export const useVCLSlideData = (loopDate, isInFolderSchedulingEnabled = false) => {
	const abortControllerRef = useRef(new AbortController());
	const {
		transformSlide, transformApiSlides,
	} = useSlideTransformation(isInFolderSchedulingEnabled);

	// Enhanced API transformation that includes VCL-specific formatting and filtering
	const transformApiSlidesForVCL = useCallback((slideItems, filterByDate = true, deckFlagOverride) => {
		const baseSlides = transformApiSlides(slideItems, deckFlagOverride);

		// Apply VCL-specific transformations
		const vclSlides = baseSlides.map((slide) => {
			return {
				...slide,
				startDate: slide.scheduled && slide.scheduling?.start_date ? formatDate(slide.scheduling.start_date) : '',
				endDate:   slide.scheduled && slide.scheduling?.end_date ? formatDate(slide.scheduling.end_date) : '',
			};
		});

		// Apply VCL-specific filtering by scheduling rules
		if (filterByDate) {
			const now = getNowForLoopDate(loopDate);
			const skipTimeChecks = isPreviewingAnotherDay(loopDate);

			return vclSlides.filter((slide) => {
				// If not scheduled, always include
				if (!slide.scheduled) return true;
				// If scheduled, use shouldSkipSlide (returns true if should be skipped)
				return !shouldSkipSlide(slide, now, skipTimeChecks);
			});
		}

		return vclSlides;
	}, [ transformApiSlides, loopDate, ]);

	// VCL-specific function to get original slides for a provider
	const getOriginalSlidesForProvider = useCallback(async (providerId, providerType, accountUUID) => {
		try {
			let response;
			if (providerType === 'published_feed') {
				// Cross-account deck read goes through the feed-authorized route,
				// which needs the loop's account UUID in the path
				if (!accountUUID) {
					return [];
				}
				response = await API.getPublishedFeedDeck(providerId, accountUUID, abortControllerRef.current.signal);
			} else {
				response = await API.findRecord('slide_deck', providerId, {
					includes: [
						'slides.folder.files',
						'slides.galleryFile',
						'slides.slide',
						// Schedule rows are only meaningful while the account flag is on
						...(isInFolderSchedulingEnabled ? [ 'slides.folderSchedules', ] : []),
					],
				}, abortControllerRef.current.signal);
			}

			if (response) {
				const slideDeck = response;

				// Sort slides by order before transformation
				const sortedSlides = (slideDeck.slides || []).sort((a, b) => {
					return (a.order || 0) - (b.order || 0);
				});

				// Published feeds: apply schedules with the publisher's flag, not ours
				const deckFlagOverride = providerType === 'published_feed'
					? slideDeck.account?.is_in_folder_scheduling_enabled
					: undefined;
				return transformApiSlidesForVCL(sortedSlides, true, deckFlagOverride);
			}
			return [];
		} catch (error) {
			if (error.name !== 'AbortError') {
				// Could log error to monitoring service here
			}
			return [];
		}
	}, [ transformApiSlidesForVCL, isInFolderSchedulingEnabled, ]);

	// VCL-specific function to process slides with backfill calculation
	const processSlidesForProvider = useCallback((slides, maxSlots = 1) => {
		const now = getNowForLoopDate(loopDate);
		const skipTimeChecks = isPreviewingAnotherDay(loopDate);

		const validSlides = slides.filter((slide) => {
			// If not scheduled, always include
			if (!slide.scheduled) return true;
			// If scheduled, use shouldSkipSlide (returns true if should be skipped)
			return !shouldSkipSlide(slide, now, skipTimeChecks);
		});

		// Ensure slides are in proper order before slicing
		const sortedSlides = validSlides.sort((a, b) => {
			return (a.order || 0) - (b.order || 0);
		});

		// Only show slides up to the provider's slot count
		const slicedSlides = sortedSlides.slice(0, maxSlots);
		const transformedSlides = slicedSlides.map(transformSlide).filter(Boolean);

		// Calculate backfill slots needed
		const usedSlots = transformedSlides.length;
		const backfillSlots = Math.max(0, maxSlots - usedSlots);

		// Add a visual "BACKFILLED" row if needed
		const childrenWithBackfill = [ ...transformedSlides, ];
		if (backfillSlots > 0) {
			childrenWithBackfill.push(createBackfillIndicator(Date.now().toString(), backfillSlots));
		}

		return childrenWithBackfill;
	}, [ loopDate, transformSlide, ]);

	return {
		transformSlide,
		transformApiSlides: transformApiSlidesForVCL,
		getOriginalSlidesForProvider,
		processSlidesForProvider,
	};
};